import { useMemo } from 'preact/hooks';
import Action from '@/widgets/ProductDetailsHero/parts/Action/Action.tsx';
import type { Product } from '@/domain/Product.ts';
import { useCart } from '@/widgets/hooks/useCart';
import { getLowestPricedSubscription } from '@/domain/Subscription.ts';
import useTranslations from '@/widgets/hooks/useTranslations';
import type { Translations } from '@/widgets/ProductDetailsHero/config';
import styles from './SubscriptionAction.module.scss';
import otherWaysToShopSectionStyles from '../../OtherWaysToShopSection.module.scss';

export type SubscriptionActionProps = {
  product: Product;
  openSubscriptionOptionsModal: () => void;
};

export default function SubscriptionAction({
  product,
  openSubscriptionOptionsModal
}: SubscriptionActionProps) {
  const { t } = useTranslations<Translations>();
  const { isLoading } = useCart();

  const lowestPricedSubscription = useMemo(
    () => getLowestPricedSubscription(product.subscriptionsByDuration),
    [product]
  );

  if (!lowestPricedSubscription || product?.isSubscriptionOnly) return null;

  const subscriptionPriceLabel = t(
    'productDetailsHero.productVariantsSection.subscriptionFromPrice', // For backwards compatibility, use label from productVariantsSection

    {
      price:
        lowestPricedSubscription.parameters.promotionalPrice ??
        lowestPricedSubscription.parameters.firstFee
    }
  );

  return (
    <Action
      disabled={isLoading.value}
      onClick={openSubscriptionOptionsModal}
      class={styles.button}
    >
      <Action.Icon icon="24/order/subscription" />
      <Action.IconLabel
        class={otherWaysToShopSectionStyles.label}
        label={t('productDetailsHero.productVariantsSection.subscriptionLabel')} // For backwards compatibility, use label from productVariantsSection
      />
      <Action.CtaLabel
        class={otherWaysToShopSectionStyles.priceLabel}
        label={subscriptionPriceLabel}
      />
      <Action.CtaIcon icon="24/symbol/chevron-right" />
    </Action>
  );
}
